import React, { useCallback, useEffect, useMemo, useState, useRef, useLayoutEffect, MouseEventHandler } from 'react';
import { Action, SwedishFrejaID_SelectDeviceModel, SwedishFrejaID_FailedModel, SwedishFrejaID_ApprovedModel, SwedishFrejaID_PendingModel, ViewVersion } from '../renditions';
import Frame from '../components/Frame';
import Footer from '../components/Footer';
import * as QRCode from 'qrcode';

import './styling';
import { Language } from 'criipto-verify-react-4';
import { LocalizedTexts, type LocalizedTextInput } from '../components/Language';
import '@fontsource/montserrat/500.css';
import '@fontsource/montserrat/700.css';
import CancelTexts from '../components/CancelTexts';

export default function SelectDevice(props: {
  model: SwedishFrejaID_SelectDeviceModel,
  action: Action,
  userAgent: string,
  version: ViewVersion
}) {
  const {version} = props
  switch (version) {
    case '2025-04-01':
    case 'initial':
    case 'unified':
      return <Version20250401 {...props} />
    default:
      assertUnreachableViewVersion(version);
  }
}

type ResumeUrl = null | 'disabled' | string

export function mkLaunchUrl(options: {
  originalLaunchUrl: string,
  userAgent: string,
  currentUrl: string,
  resumeUrl: ResumeUrl
}) {
  let redirectValue = 'null';

  if (options.resumeUrl === 'disabled') {

  } else if (options.resumeUrl) {
    redirectValue = encodeURIComponent(options.resumeUrl);
  } else {
    if (isWindowsPhone(options.userAgent) || isiOSSafari(options.userAgent)) {
      redirectValue = encodeURIComponent(options.currentUrl);
    }
  }
  return `${options.originalLaunchUrl}&originAppScheme=${redirectValue}`;
}

export const shouldPoll = function (options: {
  userAgent: string,
  resumeUrl: ResumeUrl
}) {
  if (options.resumeUrl) return true;
  if (isiOSSafari(options.userAgent)) return false;

  return true;
};

function Header( { texts }: { texts: LocalizedTextInput[] } ) {
  return (
    <h2 id="sefrejaid_selectdevice_header">
      <LocalizedTexts
        texts={texts}
      />
    </h2>
  );
}

function ErrorMessage({ error }: { error: string | null }) {
  if (error == null) return null;

  return (
    <div className="centered" id="sefrejaid_error_container">
      <LocalizedTexts
        texts={
          [
            {language: 'en', text: `Sorry, but something went wrong. Please try again in a few minutes.`},
            {language: 'sv', text: `Förlåt, men något gick fel. Försök gärna igen om en liten stund.`}
          ]
        }
      />
      <p id="error" className="error">{error}</p>
    </div>
  );
}

function FrejaIdLogo() {
  return (
    <svg className="sefrejaid-logo" xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
      <g clip-path="url(#clip0_120_1310)">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M2.34 0H18.66C19.68 0 20.5 0.82 20.5 1.84V18.16C20.5 19.18 19.68 20 18.66 20H2.34C1.32 20 0.5 19.18 0.5 18.16V1.84C0.5 0.82 1.32 0 2.34 0Z" fill="white"/>
        <path d="M15.0803 5.38L15.8203 3.42C15.9203 3.2 15.7603 3 15.5403 3H6.62029C6.50029 3 6.40029 3.08 6.34029 3.2L5.32029 5.7C5.32029 5.7 5.30029 5.76 5.30029 5.8V16.56C5.30029 16.88 5.44029 17.02 5.60029 17.02H7.96029C8.12029 17.02 8.26029 16.88 8.26029 16.72V11.62C8.26029 11.46 8.40029 11.32 8.56029 11.32H12.5003C12.6203 11.32 12.7403 11.24 12.7803 11.12L13.5803 9.18C13.6603 8.98 13.5203 8.76 13.3003 8.76H8.58029C8.42029 8.76 8.28029 8.62 8.28029 8.46V5.88C8.28029 5.72 8.42029 5.58 8.58029 5.58H14.8003C14.9203 5.58 15.0203 5.5 15.0803 5.38Z" fill="#3E3A94"/>
      </g>
      <defs>
        <clipPath id="clip0_120_1310">
          <rect width="20" height="20" fill="white" transform="translate(0.5)"/>
        </clipPath>
      </defs>
    </svg>
  );
}

function StartAppMobile({ show, launchUrl, handleLaunch, texts }: { show: boolean, launchUrl: string, handleLaunch: React.MouseEventHandler, texts: LocalizedTextInput[] }) {
    if (!show) return null;

    return (
      <a id="sefrejaid-startapp-mobile" type="button" href={launchUrl} onClick={handleLaunch}>
        <FrejaIdLogo />
        <LocalizedTexts
          texts={texts}
        />
      </a>
    );
}

function StartAppDesktop({ show, launchUrl, handleLaunch, texts }: { show: boolean, launchUrl: string, handleLaunch: React.MouseEventHandler, texts: LocalizedTextInput[] }) {
  if (!show) return null;

  return (
    <a id="sefrejaid-startapp-desktop" type="button" href={launchUrl} onClick={handleLaunch}>
      <LocalizedTexts
        texts={texts}
      />
    </a>
  );
}

function SwitchViewButton({ show, onClick }: { show: boolean, onClick: MouseEventHandler }) {
  if (!show) return null;

  return (
    <button id="sefrejaid_switch_view_button" onClick={onClick}>
      <LocalizedTexts
        texts={
          [
            {language: 'en', text: <>{'Or click here to '}<span className='underline'>{'show QR code'}</span>{' if you want to use another device'}</>},
            {language: 'sv', text: <>{'Eller klicka här för att '}<span className='underline'>{'visa QR-koden'}</span>{' om du vill använda en annan enhet'}</>}
          ]
        }
      />
    </button>
  );
}

function QrCode({ show, qrElement }: { show: boolean, qrElement: JSX.Element }) {
  if (!show) return null;

  return (
    <div id="sefrejaid-qrcode-container">
      {qrElement}
    </div>
  );
}

function QrFullScreen({ show, qrElement }: { show: boolean, qrElement: JSX.Element }) {
  if (!show) return null;

  return (
    <dialog className="qr-code-fullscreen" open>
      {qrElement}
    </dialog>
  );
}

function Spinner({ show }: { show: boolean }) {
  if (!show) return null;

  return (
    <div className="spinner"></div>
  );
}

function CancelButton({ cancelUrl }: { cancelUrl: string }) {
  return (
    <a id="sefrejaid-cancel-button" className="underline" type="button" href={cancelUrl}>
      <CancelTexts />
    </a>
  );
}

export function Version20250401(props: {
  model: SwedishFrejaID_SelectDeviceModel,
  action: Action,
  userAgent: string
}) {
  const {model, action} = props;
  const [error, setError] = useState<null | string>(null);
  const [pending, setPending] = useState(() => sessionStorage.getItem(props.model.launchUrl) === 'launched');
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [dataURL, setDataURL] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const {resumeUrl, cancelUrl} = model;
  const [view, setView] = useState<'qr' | 'same-device'>(isPhone(props.userAgent) ? 'same-device' : 'qr');

  const launchUrl = useMemo(() => {
    return mkLaunchUrl({
      originalLaunchUrl: model.launchUrl,
      userAgent: props.userAgent,
      currentUrl: window.location.href,
      resumeUrl
    });
  }, [model.launchUrl, props.userAgent, resumeUrl]);

  const poll = useCallback(async () => {
    const response = await fetch(model.pollUrl, { redirect: 'manual' });
    if (response.status >= 400) {
      const text = await response.clone().text();
      try {
        const payload : SwedishFrejaID_FailedModel = JSON.parse(text);
        if (payload.targetUrl) {
          window.location.href = payload.targetUrl;
        }
        setError(payload.message ?? text);
      } catch (err) {
        setError(text);
      }
    } else if (response.status === 202) {
      const payload : SwedishFrejaID_PendingModel = await response.json();
      if (payload.status === 'DeliveredToMobile' || payload.status === 'Opened') {
        setPending(true);
      }
      return response.status;
    } else if (response.status === 200) {
      const payload : SwedishFrejaID_ApprovedModel = await response.json();
      setPending(true);
      window.location.href = payload.targetUrl;
    } else {
      setError('Unexpected response: ' + await response.text());
    }
    return response.status;
  }, [model.pollUrl]);

  useEffect(() => {
    if (!shouldPoll({userAgent: props.userAgent, resumeUrl})) return;
    let subscribed = true;

    const recursivePoll = async (delay: number) => {
      if (!subscribed) return;
      try {
        const status = await poll();
        if (status === 202) setTimeout(() => recursivePoll(delay), delay);
      } catch (err) {
        setTimeout(() => recursivePoll(delay), delay);
      }
    }

    recursivePoll(2000);

    return () => {
      subscribed = false;
    }
  }, [model.pollUrl, props.userAgent, resumeUrl]);

  const handleLaunch = (_ : React.MouseEvent) => {
    setPending(true);
    sessionStorage.setItem(props.model.launchUrl, 'launched')
  };
  useEffect(() => {
    if (sessionStorage.getItem(props.model.launchUrl) === 'launched') {
      poll();
    }
  }, [props.model.launchUrl, poll]);

  const handleError = function (error: string) {
    setError(error);
  };

  useLayoutEffect(() => {
    QRCode.toDataURL(
      props.model.launchUrl,
      { width: 1000 }, function (err, dataURL) {
        if (err) return handleError(err.toString());
        setDataURL(dataURL);
      }
    );
  })

  const qrCodeHelpText =
    model.language.toLowerCase().startsWith('en') ? 'This is a FrejaID QR Code. You can click it to view it in full screen' :
    model.language.toLowerCase().startsWith('da') ? 'Dette er en FrejaID QR-kode. Du kan klikke på den for at se den i fuld skærm' :
    model.language.toLowerCase().startsWith('nb') ? 'Dette er en FrejaID QR-kode. Du kan klikke på den for å se den i fullskjerm' :
    'Detta är en QR-kod från FrejaID. Du kan klicka på den för att visa den i fullskärm.'

  const qrElement = (
    <button
      className="qr-code" aria-label={qrCodeHelpText}
      onClick={() => setFullscreen(val => !val)}
    >
      {dataURL ? (
        <img id="sefrejaid_qr_code" src={dataURL} ref={imageRef} />
      ) : null}
    </button>
  );

  const mainContent =
    view === 'same-device' ?
      (
        <>
          <Header
            texts={
              [
                {language: 'en', text: 'Open Freja app'},
                {language: 'sv', text: 'Öppna Freja-appen'}
              ]
            }
          />
          {error ? (
            <ErrorMessage error={error} />  
          ) : pending ? (
            <Spinner show />
          ) : (
            <React.Fragment>
              <StartAppMobile
                show
                launchUrl={launchUrl}
                handleLaunch={handleLaunch}
                texts={
                  [
                    {language: 'en', text: `Continue in Freja app`},
                    {language: 'sv', text: `Fortsätt i Freja-appen`}
                  ]
                }
              />
              <div>
                <SwitchViewButton
                  show
                  onClick={() => setView('qr')}
                />
                <CancelButton
                  cancelUrl={cancelUrl}
                />
              </div>
            </React.Fragment>
          )}
        </>
    ) : view === 'qr' ? 
      (
        <>
          <Header
            texts={
              [
                {language: 'en', text: 'Scan QR code with your Freja app'},
                {language: 'sv', text: 'Skanna QR-koden med din Freja-app'}
              ]
            }
          />
          {error ? (
            <ErrorMessage error={error} />  
          ) : pending ? (
            <Spinner show />
          ) : (
            <React.Fragment>
              <QrCode show qrElement={qrElement}/>
              <div>
                <StartAppDesktop
                  show
                  launchUrl={launchUrl}
                  handleLaunch={handleLaunch}
                  texts={
                    [
                      {language: 'en', text: <>{'Or click here to '}<span className='underline'>{'open Freja app on this device'}</span></>},
                      {language: 'sv', text: <>{'Eller klicka här för att '}<span className='underline'>{'öppna Freja-appen på den här enheten'}</span></>}
                    ]
                  }
                />
                <CancelButton
                  cancelUrl={cancelUrl}
                />
              </div>
            </React.Fragment>
          )}
        </>
      )
    : assertUnreachableView(view);

  return (
    <React.Fragment>
      <Frame className="sefrejaid-select-device centered text-center" id="sefrejaid_selectdevice_frame">
        {mainContent}
      </Frame>

      <Footer language={model.language as Language} />
      <QrFullScreen show={fullscreen && dataURL !== null && !error && !pending} qrElement={qrElement} />
    </React.Fragment>
  );
}

function assertUnreachableViewVersion(x: never): never {
  throw new Error(`Unandled viewVersion: ${x}`);
}
function assertUnreachableView(x: never): never {
  throw new Error(`Unandled view: ${x}`);
}

// Legacy javascript, TODO: Refactor
const testUA = function (pattern: RegExp, userAgent: string) {
  return pattern.test(userAgent);
};
const isiOS = function (userAgent: string) {
  return testUA(/iPad|iPhone|iPod/, userAgent) && !("MSStream" in window);
};
const isWindowsPhone = function (userAgent: string) { return testUA(/Windows Phone/i, userAgent); };
const isiOSSafari = function (userAgent: string) {
  return isiOS(userAgent) && !testUA(/ CriOS\/[.0-9]*/, userAgent);
};
const isAndroid = function (userAgent: string) { return testUA(/Android/, userAgent); };
const isPhone = function (userAgent: string) { return isiOS(userAgent) || isWindowsPhone(userAgent) || isAndroid(userAgent); }